import i18n, { BackendModule } from "i18next";
import { initReactI18next } from "react-i18next";
import { resources, LANGUAGES, Language, LazyLanguage } from "./resources";

const isLazy = (lng: string): lng is LazyLanguage => lng === "fr" || lng === "pt";

/**
 * fr/pt are not in the main bundle. Production loads the generated JSON
 * (one small chunk per language); development builds the tree live from the
 * ns files. `process.env.NODE_ENV` is a build-time constant, so webpack drops
 * the unused branch and its import.
 */
const loadLazyLanguage = (lng: LazyLanguage): Promise<Record<string, unknown>> =>
  process.env.NODE_ENV === "production"
    ? lng === "fr"
      ? import(/* webpackChunkName: "lang-fr" */ "./lang/fr.json").then((m) => m.default)
      : import(/* webpackChunkName: "lang-pt" */ "./lang/pt.json").then((m) => m.default)
    : import("./lazyTree").then((m) => m.buildTree(lng));

/** Minimal i18next backend: only the fr/pt `translation` trees are fetched. */
const lazyLanguages: BackendModule = {
  type: "backend",
  init() {},
  read(language, namespace, callback) {
    if (namespace === "translation" && isLazy(language)) {
      loadLazyLanguage(language).then(
        (data) => callback(null, data),
        (err) => callback(err, false)
      );
    } else {
      // en/es are bundled; careers/legal/admin are registered by their chunks.
      callback(null, {});
    }
  },
};

const storedLang = (() => {
  try {
    return localStorage.getItem("i18nextLng");
  } catch {
    return null;
  }
})();
const browserLang = (navigator.language || "en").split("-")[0];

/** Keep <html lang> on the resolved two-letter language. */
const syncHtmlLang = () => {
  const lng = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  document.documentElement.lang = lng;
};

i18n.on("initialized", syncHtmlLang);
i18n.on("languageChanged", syncHtmlLang);

i18n
  .use(lazyLanguages)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    lng: storedLang || browserLang,
    fallbackLng: "en",
    supportedLngs: [...LANGUAGES],
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    interpolation: { escapeValue: false },
  });

syncHtmlLang();

/**
 * Register a namespace that ships inside a lazy route chunk (careers, legal).
 * Separate i18next namespaces so they never create a partial fr/pt
 * `translation` bundle (which would stop the fr/pt tree from loading).
 */
export function registerNamespace(ns: string, data: Record<Language, object>) {
  for (const lng of LANGUAGES) {
    i18n.addResourceBundle(lng, ns, data[lng], true, true);
  }
}

export default i18n;
